/**
 * Partnership Pacing maths, ported 1:1 from the KingsPacingDashboardV2-Lux mockup.
 *
 * Pure functions (no React, no DOM) over the named-field rows of GET dashboard/pacing/:
 *   { window: 'cur' | 'pw', month: 'yyyy-MM', partner, channel, location,
 *     exposures, duration, impressions, meValue }
 * `current` / `prior` are the payload's { games, local, national } counts.
 */
import _ from 'lodash';

export const CUR = 'cur';
export const PRIOR = 'pw';
export const DASH = '—';

export function big(v) {
    return v >= 1e9 ? (v / 1e9).toFixed(2) + 'B'
        : v >= 1e6 ? (v / 1e6).toFixed(2) + 'M'
            : v >= 1e3 ? (v / 1e3).toFixed(0) + 'K'
                : String(Math.round(v));
}

// Declaration order is the Metric select order; METRIC_ORDER is the KPI card order.
export const METRICS = {
    mev: { key: 'meValue', label: 'ME Value', fmt: v => '$' + big(v) },
    imp: { key: 'impressions', label: 'Raw Impressions', fmt: big },
    exp: { key: 'exposures', label: 'Exposures', fmt: v => (v >= 1000 ? big(v) : Math.round(v).toLocaleString()) },
    dur: { key: 'duration', label: 'Duration', fmt: v => (v >= 3600 ? (v / 3600).toFixed(1) + 'h' : Math.round(v) + 's') },
};
export const METRIC_ORDER = ['exp', 'dur', 'imp', 'mev'];

export const KPI_ALERT_DELTA = -20; // KPI delta (%) vs prior at or below this => alert colour

export const STATUS_COLOR = {
    ok: 'var(--ok)', watch: 'var(--watch)', alert: 'var(--alert)',
    ahead: 'var(--ahead)', neu: 'var(--new)', off: 'var(--faint)', gone: 'var(--gone)',
};

// hasBase: anything delivered in the prior window. Without it the row is "New" (no comparison possible).
// finished: the season is complete, so the row is a final outcome (Achieved at >= 100%) rather than a pace band.
export function statusOf(pace, hasBase, finished) {
    if (!hasBase) return { label: 'New', key: 'neu' };
    if (pace === null) return { label: DASH, key: 'off' };
    if (finished) return pace >= 100 ? { label: 'Achieved', key: 'ok' } : { label: 'Not achieved', key: 'alert' };
    if (pace < 20) return { label: 'Underactive', key: 'off' };
    if (pace < 80) return { label: 'Alert', key: 'alert' };
    if (pace < 95) return { label: 'Watch', key: 'watch' };
    if (pace <= 120) return { label: 'On track', key: 'ok' };
    if (pace < 200) return { label: 'Ahead', key: 'ahead' };
    return { label: 'Expanded', key: 'neu' };
}

export const formatMetric = (metric, v) => METRICS[metric].fmt(v);
export const formatDelta = d => (d === null ? DASH : (d >= 0 ? '+' : '') + d.toFixed(1) + '%');
export const formatPace = pace => (pace === null ? 'NEW' : (pace - 100 >= 0 ? '+' : '') + (pace - 100).toFixed(0) + '%');

// Season labels as the mockup prints them: 'YYYY-YYYY' => 'YYYY-YY' (crest, legend, select) / 'YY-YY' (compact)
export const longSeason = season => season.split('-').map((y, i) => (i ? y.slice(-2) : y)).join('-');
export const shortSeason = season => season.split('-').map(y => y.slice(-2)).join('-');

// 'MM' => English month name; style 'short' (chart: 'Jul') or 'long' (Months filter: 'July')
const monthName = (mm, style) => new Date(Date.UTC(2000, Number(mm) - 1, 1)).toLocaleString('en-US', { month: style, timeZone: 'UTC' });

// Season order (Jul -> Jun): [value 'MM', label]
const SEASON_MM = ['07', '08', '09', '10', '11', '12', '01', '02', '03', '04', '05', '06'];
export const MONTHS = SEASON_MM.map(mm => [mm, monthName(mm, 'long')]);

// Each filter is a list; empty = no filter. Months match by calendar month ('MM') in both windows.
const matches = (list, value) => !list.length || list.includes(value);
export const filterRows = (rows, { partners = [], channels = [], locations = [], months = [] }) => rows.filter(r =>
    matches(partners, r.partner)
    && matches(channels, r.channel)
    && matches(locations, r.location)
    && matches(months, r.month.slice(5)));

// Games played per window in the selected months (Local/National counts); all months => the payload's season counts.
// No gamesByMonth (older backend) => zero counts.
export function gamesFor(data, months) {
    if (!months.length) return { current: data.current, prior: data.prior };
    const sum = win => {
        const total = { games: 0, local: 0, national: 0 };
        (data.gamesByMonth || []).filter(g => g.window === win && months.includes(g.month.slice(5))).forEach(g => {
            total.games += g.games;
            total.local += g.local;
            total.national += g.national;
        });
        return total;
    };
    return { current: sum(CUR), prior: sum(PRIOR) };
}

export function sumMetric(rows, win, field) {
    let t = 0;
    for (const r of rows) {
        if (r.window === win) t += r[field];
    }
    return t;
}

// Hero cards: season totals and their delta vs the prior window's totals (same games played, so totals compare).
export function kpis(rows) {
    return METRIC_ORDER.map(m => {
        const field = METRICS[m].key;
        const total = sumMetric(rows, CUR, field);
        const prior = sumMetric(rows, PRIOR, field);
        const deltaPct = prior ? (total / prior - 1) * 100 : null;
        const color = deltaPct === null ? 'var(--faint)'
            : deltaPct >= 0 ? 'var(--ok)'
                : deltaPct <= KPI_ALERT_DELTA ? 'var(--alert)' : 'var(--watch)';
        return { key: m, label: METRICS[m].label, total, deltaPct, color };
    });
}

// Each window spans about a year, so current and prior pair on month-of-year ('MM'), as the mockup paired them on month name.
// A season can hold the same calendar month twice (2025-26 runs Jul 2025 - Jul 2026); it is one chart column, values combined.
const monthOfYear = month => month.slice(5);

// Distinct months-of-year of the current window (the prior window's when current is empty), in season order Jul -> Jun.
export function chartMonths(rows) {
    const months = win => new Set(rows.filter(r => r.window === win).map(r => monthOfYear(r.month)));
    const cur = months(CUR);
    const present = cur.size ? cur : months(PRIOR);
    return SEASON_MM.filter(mm => present.has(mm));
}

// months: 'MM' list from chartMonths
export function monthlySeries(rows, metric, months) {
    const field = METRICS[metric].key;
    const byMonth = {}; // 'MM' => { p, c }
    for (const r of rows) {
        const mm = monthOfYear(r.month);
        if (!byMonth[mm]) byMonth[mm] = { p: 0, c: 0 };
        if (r.window === CUR) byMonth[mm].c += r[field];
        else if (r.window === PRIOR) byMonth[mm].p += r[field];
    }
    const series = months.map(mm => {
        const acc = byMonth[mm] || { p: 0, c: 0 };
        return { month: mm, label: monthName(mm, 'short'), p: acc.p, c: acc.c };
    });
    const max = Math.max(1, ...series.map(s => Math.max(s.p, s.c)));
    return { series, max };
}

// The table lists partners, or the single chosen partner's locations.
export const groupField = partners => (partners.length === 1 ? 'location' : 'partner');

const NOT_ACTIVE = { label: 'Not Active', key: 'gone' };
const delivered = r => _.some(METRICS, m => r[m.key] > 0);

// Table keys that delivered in either of the two previous whole seasons (data.earlierRows) but nothing this season to date.
// Months are ignored (a historical status); partner/channel/location filters apply. No earlierRows (older backend) => none.
export function notActiveKeys(data, { partners, channels, locations }) {
    const dim = groupField(partners);
    const keys = list => new Set(list.filter(r => matches(partners, r.partner) && matches(channels, r.channel)
        && matches(locations, r.location) && delivered(r)).map(r => r[dim]));
    const current = keys(data.rows.filter(r => r.window === CUR));
    return new Set([...keys(data.earlierRows || [])].filter(key => !current.has(key)));
}

// notActive: keys listed as Not Active (no pace) whatever their other status; added with zero values when absent from rows.
export function tableRows(rows, metric, partners, finished = false, notActive = new Set()) {
    const dim = groupField(partners);
    const field = METRICS[metric].key;
    const groups = new Map(); // first-seen order
    for (const r of rows) {
        let g = groups.get(r[dim]);
        if (!g) {
            g = { c: 0, p: 0, priorMev: 0 };
            groups.set(r[dim], g);
        }
        if (r.window === CUR) g.c += r[field];
        else if (r.window === PRIOR) {
            g.p += r[field];
            g.priorMev += r.meValue;
        }
    }
    notActive.forEach(key => {
        if (!groups.has(key)) groups.set(key, { c: 0, p: 0, priorMev: 0 });
    });
    const out = [];
    groups.forEach(({ c, p, priorMev }, key) => {
        if (notActive.has(key)) {
            out.push({ key, c, p, pace: null, status: NOT_ACTIVE });
            return;
        }
        const pace = p > 0 ? c / p * 100 : null;
        out.push({ key, c, p, pace, status: statusOf(pace, priorMev > 0, finished) });
    });
    // Pace ascending already orders the pace-band statuses (Underactive < Alert < ... < Expanded).
    // "New" (no prior delivery) has no pace; keep it explicitly last. Rows without a pace
    // (New, or "—") are ordered by current value descending so the biggest ones lead.
    return out.sort((a, b) =>
        (a.status.label === 'New') - (b.status.label === 'New')
        || (a.pace === null) - (b.pace === null)
        || (a.pace === null ? b.c - a.c : a.pace - b.pace));
}

// Deviation from 100% pace on a +-100 rail (no-pace rows get a fixed 60).
export function railGeometry(pace) {
    const dev = pace === null ? 60 : Math.max(-100, Math.min(100, pace - 100));
    const width = Math.abs(dev) / 100 * 50;
    return { left: dev < 0 ? 50 - width : 50, width };
}

// Option lists, alphabetical and case-insensitive.
const alpha = (a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' });
export function optionLists(rows) {
    return {
        partners: _.uniq(rows.map(r => r.partner)).sort(alpha),
        channels: _.uniq(rows.map(r => r.channel)).sort(alpha),
        locations: _.uniq(rows.map(r => r.location)).sort(alpha),
    };
}

// Status column order, worst to best (Not Active first); finished-season outcomes share the scale; '—' and New sort after every band.
export const STATUS_RANK = {
    'Not Active': -1, Underactive: 0, Alert: 1, Watch: 2, 'On track': 3, Ahead: 4, Expanded: 5,
    'Not achieved': 0, Achieved: 1, [DASH]: 6, New: 7,
};

// Sortable numeric columns; null = missing, listed last in both directions. Δ is pace - 100, so it orders like pace.
const SORT_VALUE = {
    pace: r => r.pace,
    delta: r => r.pace,
    c: r => r.c,
    p: r => (r.status.label === 'New' ? null : r.p),
};
const nullsLast = (a, b, sign) => (a === null) - (b === null) || (a === null ? 0 : sign * (a - b));

// sort: null (keep tableRows' default order) or { column: 'key'|'pace'|'c'|'p'|'delta'|'status', dir: 'asc'|'desc' }.
// Final tie-break is the key A->Z, so equal rows never jump.
export function sortTableRows(list, sort) {
    if (!sort) return list;
    const sign = sort.dir === 'asc' ? 1 : -1;
    const value = SORT_VALUE[sort.column];
    const compare = sort.column === 'key' ? (a, b) => sign * alpha(a.key, b.key)
        : sort.column === 'status' ? (a, b) => sign * (STATUS_RANK[a.status.label] - STATUS_RANK[b.status.label])
            || nullsLast(a.pace, b.pace, sign)
            : (a, b) => nullsLast(value(a), value(b), sign);
    return list.slice().sort((a, b) => compare(a, b) || alpha(a.key, b.key));
}
